import type { SidebarTreeNode, SidebarTreeSection } from '@/components/layout/sidebar/sidebar-tree.types'

export type MockDriveFileKind = 'doc' | 'folder' | 'md' | 'image' | 'shortcut' | 'pdf'

export type MockDriveFileMeta = {
  fileId: string
  type: MockDriveFileKind
  mimeType: string
  shortcutTarget?: { id: string; mimeType: string; name?: string } | null
}

type MockDriveFile = {
  id: string
  name: string
  type: MockDriveFileKind
  modified: string
  children?: MockDriveFile[]
  /** Present when type === 'shortcut' */
  shortcutTarget?: { id: string; mimeType: string; name?: string }
}

export type MockSharedDrive = {
  id: string
  name: string
  files: MockDriveFile[]
}

export function mockMimeForKind(type: MockDriveFileKind): string {
  switch (type) {
    case 'doc':
      return 'application/vnd.google-apps.document'
    case 'md':
      return 'text/markdown'
    case 'folder':
      return 'application/vnd.google-apps.folder'
    case 'image':
      return 'image/png'
    case 'shortcut':
      return 'application/vnd.google-apps.shortcut'
    case 'pdf':
      return 'application/pdf'
    default:
      return ''
  }
}

// --- Mock My Drive -----------------------------------------------------------

export const MOCK_MY_DRIVE: MockDriveFile[] = [
  {
    id: 'f1',
    name: 'My Writing',
    type: 'folder',
    modified: '2 days ago',
    children: [
      { id: 'd1', name: 'The Architects (Book Draft).gdoc', type: 'doc', modified: '3 hours ago' },
      { id: 'd2', name: 'Chapter Notes.gdoc', type: 'doc', modified: 'yesterday' },
      { id: 'd3', name: 'Research Links.md', type: 'md', modified: '4 days ago' },
      {
        id: 'img1',
        name: 'Cover sketch.png',
        type: 'image',
        modified: '1 day ago',
      },
      {
        id: 'sc1',
        name: 'Bellyfit (shortcut)',
        type: 'shortcut',
        modified: 'today',
        shortcutTarget: {
          id: 'folder-bellyfit',
          mimeType: 'application/vnd.google-apps.folder',
          name: 'Bellyfit',
        },
      },
      {
        id: 'pdf1',
        name: 'Style guide.pdf',
        type: 'pdf',
        modified: '5 days ago',
      },
    ],
  },
  {
    id: 'f2',
    name: 'Projects',
    type: 'folder',
    modified: 'today',
    children: [
      { id: 'd4', name: 'Editorial Feedback - May.gdoc', type: 'doc', modified: 'today' },
      { id: 'd6', name: 'Timeline.gdoc', type: 'doc', modified: '3 days ago' },
    ],
  },
  { id: 'd5', name: 'Quick Notes.gdoc', type: 'doc', modified: 'today' },
]

// --- Mock Shared Drives -------------------------------------------------------

export const MOCK_SHARED_DRIVES: MockSharedDrive[] = [
  {
    id: 'sd1',
    name: 'Hop Clients',
    files: [
      {
        id: 'sdf1',
        name: 'GGSC',
        type: 'folder',
        modified: 'today',
        children: [
          { id: 'sdf1a', name: 'Content Plan Q3.gdoc', type: 'doc', modified: 'today' },
          { id: 'sdf1b', name: 'Meeting Notes.gdoc', type: 'doc', modified: '2 days ago' },
        ],
      },
      { id: 'sdf2', name: 'Hop Client Brief Template.gdoc', type: 'doc', modified: '3 days ago' },
    ],
  },
  {
    id: 'sd2',
    name: 'Hop General',
    files: [
      { id: 'sdf3', name: 'Team Handbook.gdoc', type: 'doc', modified: '1 week ago' },
      { id: 'sdf4', name: 'Design Assets', type: 'folder', modified: '2 days ago', children: [] },
      { id: 'sdf5', name: 'Company OKRs.gdoc', type: 'doc', modified: '5 days ago' },
    ],
  },
  {
    id: 'sd3',
    name: 'Hop Identity',
    files: [
      { id: 'sdf6', name: 'Brand Guidelines.gdoc', type: 'doc', modified: '1 week ago' },
      { id: 'sdf7', name: 'Logo Files', type: 'folder', modified: '2 weeks ago', children: [] },
    ],
  },
]

// --- Mock Recent -------------------------------------------------------------

export const MOCK_RECENT: MockDriveFile[] = [
  { id: 'd1', name: 'The Architects (Book Draft).gdoc', type: 'doc', modified: '3 hours ago' },
  { id: 'd5', name: 'Quick Notes.gdoc', type: 'doc', modified: 'today' },
  { id: 'd4', name: 'Editorial Feedback - May.gdoc', type: 'doc', modified: 'today' },
  { id: 'sdf3', name: 'Team Handbook.gdoc', type: 'doc', modified: '1 week ago' },
  { id: 'sdf1a', name: 'Content Plan Q3.gdoc', type: 'doc', modified: 'today' },
  { id: 'd2', name: 'Chapter Notes.gdoc', type: 'doc', modified: 'yesterday' },
  { id: 'img1', name: 'Cover sketch.png', type: 'image', modified: '1 day ago' },
]

// --- Mapping helpers ---------------------------------------------------------

function toMeta(file: MockDriveFile): MockDriveFileMeta {
  return {
    fileId: file.id,
    type: file.type,
    mimeType: mockMimeForKind(file.type),
    shortcutTarget: file.shortcutTarget ?? null,
  }
}

export function mapMockDriveToTree(files: MockDriveFile[]): SidebarTreeNode<MockDriveFileMeta>[] {
  return files.map((file) => {
    const isFolder = file.type === 'folder'
    const icon =
      isFolder
        ? 'folder'
        : file.type === 'md'
          ? 'md'
          : file.type === 'image'
            ? 'file'
            : file.type === 'shortcut'
              ? 'gshortcut'
              : file.type === 'pdf'
                ? 'file'
                : 'gdoc'
    return {
      id: file.id,
      kind: isFolder ? 'folder' : 'file',
      label: file.name,
      icon,
      subtitle: file.modified,
      badge: file.type === 'shortcut' ? 'Shortcut' : undefined,
      meta: toMeta(file),
      children: file.children ? mapMockDriveToTree(file.children) : undefined,
    }
  })
}

export function mapMockSharedDrivesToFolderNodes(): SidebarTreeNode<MockDriveFileMeta>[] {
  return MOCK_SHARED_DRIVES.map((drive) => ({
    id: drive.id,
    kind: 'folder' as const,
    label: drive.name,
    icon: 'folder' as const,
    meta: {
      fileId: drive.id,
      type: 'folder' as const,
      mimeType: mockMimeForKind('folder'),
    },
    children: mapMockDriveToTree(drive.files),
  }))
}

export function mapMockSharedDrivesToSections(): SidebarTreeSection<MockDriveFileMeta>[] {
  return MOCK_SHARED_DRIVES.map((drive) => ({
    id: drive.id,
    label: drive.name,
    nodes: mapMockDriveToTree(drive.files),
  }))
}

/**
 * @deprecated Fake Google Doc iframe URLs break harness preview ("file does not exist").
 * Use HarnessDrivePreviewStub instead - kept only so old imports fail loudly if called.
 */
export function getMockDrivePreviewUrl(
  _fileId: string,
  _type: MockDriveFileMeta['type'],
): string | null {
  return null
}
